import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { MoreHorizontal } from 'lucide-react';
import { useAgentRoster, useAgentStats, usePersonas, useRecruitAgent, useSetPersonaStatus } from '@diggens/shared-hooks';
import type { AgentRosterEntry, AgentSlug, TenantPersonaSummary } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { JobDialog } from './JobDialog';
import { PersonaDefinitionDialog } from './PersonaDefinitionDialog';
import { agentLabel } from '@diggens/chat';
import { CORE_AGENTS, TeamCard, TeamGrid, type StatsState } from './TeamCard';

/** A persona's source and version, after its name. */
function PersonaBadges({ entry }: { entry: AgentRosterEntry }) {
  if (entry.source === 'compiled') return null;
  return (
    <>
      <Chip label={entry.source === 'hire' ? 'Hired' : `From app ${entry.source.slice('app:'.length)}`} size="small" variant="outlined" />
      {entry.version !== null && <Chip label={`v${entry.version}`} size="small" variant="outlined" />}
    </>
  );
}

/**
 * The workspace's team: the core agents, the specialists it recruited (compiled ones and personas hired or shipped by
 * apps), dashed cards for the ones it can recruit, and the personas disabled earlier, which an admin can activate
 * again. Dismissing sits in a recruited card's menu and asks first; disabling a persona takes it off the roster.
 */
export function YourTeam({ onOpenRun }: { onOpenRun: (runId: string) => void }) {
  const navigate = useNavigate();
  const { data: roster, isLoading: rosterLoading, error: rosterError } = useAgentRoster();
  const { data: stats, isLoading: statsLoading, error: statsError } = useAgentStats();
  const { data: personas } = usePersonas();
  const recruit = useRecruitAgent();
  const setPersonaStatus = useSetPersonaStatus();
  const [jobFor, setJobFor] = useState<AgentRosterEntry | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; entry: AgentRosterEntry } | null>(null);
  const [dismissing, setDismissing] = useState<AgentRosterEntry | null>(null);
  const [disabling, setDisabling] = useState<AgentRosterEntry | null>(null);
  const [viewing, setViewing] = useState<AgentRosterEntry | TenantPersonaSummary | null>(null);

  if (rosterLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (rosterError || !roster) return <Alert severity="error">{rosterError instanceof Error ? rosterError.message : 'Could not load your agents'}</Alert>;

  const statsFor = (agent: AgentSlug) => stats?.find((entry) => entry.agent === agent);
  const statsState: StatsState = stats ? 'ready' : statsLoading ? 'loading' : 'error';
  const recruited = roster.filter((entry) => entry.enabled);
  const available = roster.filter((entry) => !entry.enabled);
  // A persona version disabled earlier whose slug has no active version now: it can come back as it was
  const activeSlugs = new Set(roster.filter((entry) => entry.source !== 'compiled').map((entry) => entry.agent));
  const disabledPersonas = (personas ?? []).filter((persona) => persona.status === 'disabled' && !activeSlugs.has(persona.slug));
  const viewActivity = (agent: AgentSlug) => (
    <Button size="small" onClick={() => navigate(`/activity?agent=${encodeURIComponent(agent)}`)}>
      View activity
    </Button>
  );
  const viewDefinition = (entry: AgentRosterEntry) =>
    entry.definition ? (
      <Button size="small" onClick={() => setViewing(entry)}>
        View definition
      </Button>
    ) : null;

  return (
    <Stack spacing={2}>
      {statsError && <Alert severity="error">{statsError instanceof Error ? statsError.message : 'Could not load what your agents are doing'}</Alert>}
      {recruit.error && <Alert severity="error">{recruit.error.message}</Alert>}
      {setPersonaStatus.error && <Alert severity="error">{setPersonaStatus.error.message}</Alert>}
      <TeamGrid>
        {CORE_AGENTS.map((core) => (
          <TeamCard
            key={core.agent}
            name={agentLabel(core.agent)}
            description={core.description}
            writes={core.writes}
            stats={statsFor(core.agent)}
            statsState={statsState}
            onOpenRun={onOpenRun}
            actions={
              <>
                {core.chat && (
                  <Button size="small" variant="contained" onClick={() => navigate(core.chat!.path)}>
                    {core.chat.label}
                  </Button>
                )}
                {viewActivity(core.agent)}
              </>
            }
          />
        ))}
        {recruited.map((entry) => (
          <TeamCard
            key={entry.agent}
            name={entry.displayName}
            description={entry.description}
            writes={entry.writes}
            approvalFree={entry.approvalFree}
            badges={<PersonaBadges entry={entry} />}
            stats={statsFor(entry.agent)}
            statsState={statsState}
            onOpenRun={onOpenRun}
            actions={
              <>
                <Button size="small" variant="contained" onClick={() => setJobFor(entry)}>
                  Give work
                </Button>
                {viewActivity(entry.agent)}
                {viewDefinition(entry)}
                <IconButton size="small" aria-label={`More for ${entry.displayName}`} onClick={(event) => setMenu({ anchor: event.currentTarget, entry })} sx={{ ml: 'auto' }}>
                  <MoreHorizontal size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
                </IconButton>
              </>
            }
          />
        ))}
        {available.map((entry) => (
          <TeamCard
            key={entry.agent}
            available
            name={entry.displayName}
            description={entry.description}
            writes={entry.writes}
            approvalFree={entry.approvalFree}
            badges={<PersonaBadges entry={entry} />}
            stats={statsFor(entry.agent)}
            statsState={statsState}
            onOpenRun={onOpenRun}
            actions={
              <>
                <Button size="small" variant="outlined" disabled={recruit.isPending} onClick={() => recruit.mutate({ agent: entry.agent, enabled: true })}>
                  Recruit
                </Button>
                {viewDefinition(entry)}
              </>
            }
          />
        ))}
      </TeamGrid>

      {disabledPersonas.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Disabled personas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Personas taken off the team, kept as they were. Activating one puts it back on the team as that version.
          </Typography>
          <Stack spacing={1}>
            {disabledPersonas.map((persona) => (
              <Stack key={persona.id} direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {persona.definition.displayName}
                </Typography>
                <Chip label={persona.source === 'hire' ? 'Hired' : `From app ${persona.source.slice('app:'.length)}`} size="small" variant="outlined" />
                <Chip label={`v${persona.version}`} size="small" variant="outlined" />
                <Button size="small" onClick={() => setViewing(persona)}>
                  View definition
                </Button>
                <Button size="small" variant="outlined" disabled={setPersonaStatus.isPending} onClick={() => setPersonaStatus.mutate({ personaId: persona.id, action: 'activate' })}>
                  Activate
                </Button>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) setDismissing(menu.entry);
            setMenu(null);
          }}
        >
          Dismiss from team
        </MenuItem>
        {menu?.entry.personaId && (
          <MenuItem
            onClick={() => {
              if (menu) setDisabling(menu.entry);
              setMenu(null);
            }}
          >
            Disable persona
          </MenuItem>
        )}
      </Menu>

      {disabling && disabling.personaId && (
        <Dialog open onClose={() => setDisabling(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Disable {disabling.displayName}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              It leaves the team for every user and takes no more jobs or plan steps; work it already started finishes. Its runs stay in Activity, and you can activate this version again here.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisabling(null)}>Keep</Button>
            <Button color="error" disabled={setPersonaStatus.isPending} onClick={() => setPersonaStatus.mutate({ personaId: disabling.personaId!, action: 'disable' }, { onSettled: () => setDisabling(null) })}>
              Disable
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {viewing && viewing.definition && (
        <PersonaDefinitionDialog definition={viewing.definition} source={viewing.source} version={viewing.version} onClose={() => setViewing(null)} />
      )}

      {dismissing && (
        <Dialog open onClose={() => setDismissing(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Dismiss {dismissing.displayName}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              It stops taking new jobs and plan steps; work it already started finishes. You can recruit it again here.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDismissing(null)}>Keep</Button>
            <Button color="error" disabled={recruit.isPending} onClick={() => recruit.mutate({ agent: dismissing.agent, enabled: false }, { onSettled: () => setDismissing(null) })}>
              Dismiss
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {jobFor && (
        <JobDialog
          key={jobFor.agent}
          agent={jobFor}
          onClose={() => setJobFor(null)}
          onStarted={(runId) => {
            setJobFor(null);
            onOpenRun(runId);
          }}
        />
      )}
    </Stack>
  );
}
